import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Icon from '../Icon/Icon';

/**
 * `Notices` is a component that can be used for displaying inline error, warning, info and success messages to the user.
 * The message will be rendered with an icon and colors based on the type set (error, warning, info or success). The
 * component will automatically adapt to the message length.
 *
 * If you provide more than one message to the component the component will display the most important message determined by
 * this priority:
 * 1. error
 * 2. warning
 * 3. success
 * 4. info
 *
 * The component has a top border by default which can be changed using CSS. The component should normally have a border
 * between the message and the component that it displays the message for.
 *
 * Good messages should:
 * 1. Let the user know what the problem is in a non-technical manner.
 * 2. Let the user know how the problem can be fixed.
 * 3. Be written in a tone that is consistent with other dialog messages. Write for non-technical humans.
 *
 * Use info and success messages sparingly. This will make the error and warning states pop out more.
 *
 * @property {string} className The class name of your component. Will be appended to "core-notice".
 * @property {node} errorMessage The error message. Will be displayed with an error icon and red colors.
 * @property {node} infoMessage The info message. Will be displayed with an info icon and blue colors.
 * @property {func} onClear A callback that is called when the user clicks on the close button. The close button will only
 * be shown when this callback is set. It is the responsibility of the callback function to actually remove the message.
 * @property {node} successMessage The success message. Will be displayed with a success icon and green colors.
 * @property {node} warningMessage The warning message. Will be displayed with a warning icon and orange colors.
 */
 export default class Notice extends PureComponent {

  static displayName = 'Notice';

  static propTypes = {
    errorMessage: PropTypes.node,
    infoMessage: PropTypes.node,
    onClear: PropTypes.func,
    successMessage: PropTypes.node,
    warningMessage: PropTypes.node,
  };

  render() {
    const { errorMessage, infoMessage, onClear, successMessage, warningMessage } = this.props;
    let type;
    let message;

    if (errorMessage) {
      type = "error";
      message = errorMessage;
    }
    else if (warningMessage) {
      type = "warning";
      message = warningMessage;
    }
    else if (successMessage) {
      type = "success";
      message = successMessage;
    }
    else if (infoMessage) {
      type = "info";
      message = infoMessage;
    } else {
      return false;
    }
    return (
      <div className={cx('core-notice', type)}>
        <Icon name={type} />
        <span className={cx('core-notice-message')}>
          {message}
        </span>
        {onClear && <Icon name="close" className="close" onClick={onClear} />}
      </div>
    );
  }

}